import React from 'react';
import { X, AlertTriangle, Car, Shield, Clock, PenTool as Tool, FileText } from 'lucide-react';

interface SelfDriveTermsModalProps {
  isOpen: boolean;
  onClose: () => void;
  onAccept: () => void;
}

const SelfDriveTermsModal: React.FC<SelfDriveTermsModalProps> = ({ isOpen, onClose, onAccept }) => {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
        <div className="fixed inset-0 transition-opacity bg-black/60 backdrop-blur-sm" aria-hidden="true" onClick={onClose} />

        <div className="inline-block w-full max-w-4xl my-8 overflow-hidden text-left align-middle transition-all transform bg-white rounded-lg shadow-xl">
          <div className="px-6 py-4 border-b border-gray-200">
            <div className="flex items-center justify-between">
              <div className="flex items-center">
                <FileText className="w-6 h-6 text-ta-green mr-2" />
                <h3 className="text-xl font-semibold text-gray-900">Self-Drive Safari Terms & Conditions</h3>
              </div>
              <button onClick={onClose} className="text-gray-400 hover:text-gray-500">
                <X className="w-6 h-6" />
              </button>
            </div>
          </div>

          <div className="px-6 py-4 max-h-[70vh] overflow-y-auto">
            <div className="space-y-6">
              {/* Eligibility & Requirements */}
              <section>
                <h4 className="flex items-center text-lg font-semibold text-gray-900 mb-3">
                  <Shield className="w-5 h-5 text-ta-green mr-2" />
                  Eligibility & Requirements
                </h4>
                <div className="bg-gray-50 p-4 rounded-lg space-y-2">
                  <p className="text-sm text-gray-600">
                    <strong>Driver Requirements:</strong>
                  </p>
                  <ul className="list-disc pl-5 text-sm text-gray-600 space-y-1">
                    <li>Minimum age: 23 years old</li>
                    <li>Valid driver's license held for at least 2 years</li>
                    <li>International Driving Permit (IDP) required for non-English licenses</li>
                    <li>Clean driving record for the past 2 years</li>
                  </ul>
                  <p className="text-sm text-gray-600 mt-3">
                    <strong>Insurance Requirements:</strong>
                  </p>
                  <ul className="list-disc pl-5 text-sm text-gray-600 space-y-1">
                    <li>Comprehensive insurance coverage mandatory</li>
                    <li>Personal accident insurance recommended</li>
                    <li>Third-party liability coverage included</li>
                    <li>Additional insurance options available</li>
                  </ul>
                </div>
              </section>

              {/* Rental & Payment */}
              <section>
                <h4 className="flex items-center text-lg font-semibold text-gray-900 mb-3">
                  <Car className="w-5 h-5 text-ta-green mr-2" />
                  Rental & Payment Policies
                </h4>
                <div className="bg-gray-50 p-4 rounded-lg space-y-2">
                  <p className="text-sm text-gray-600">
                    <strong>Booking & Payment:</strong>
                  </p>
                  <ul className="list-disc pl-5 text-sm text-gray-600 space-y-1">
                    <li>30% deposit required at booking</li>
                    <li>Full payment due 14 days before pickup</li>
                    <li>Security deposit: $500 (refundable)</li>
                    <li>Accepted payments: Credit card, bank transfer</li>
                  </ul>
                  <p className="text-sm text-gray-600 mt-3">
                    <strong>Cancellation Policy:</strong>
                  </p>
                  <ul className="list-disc pl-5 text-sm text-gray-600 space-y-1">
                    <li>30+ days: Full refund minus processing fee</li>
                    <li>14-29 days: 70% refund</li>
                    <li>7-13 days: 50% refund</li>
                    <li>Less than 7 days: No refund</li>
                  </ul>
                </div>
              </section>

              {/* Vehicle Use */}
              <section>
                <h4 className="flex items-center text-lg font-semibold text-gray-900 mb-3">
                  <Clock className="w-5 h-5 text-ta-green mr-2" />
                  Vehicle Use & Responsibility
                </h4>
                <div className="bg-gray-50 p-4 rounded-lg space-y-2">
                  <p className="text-sm text-gray-600">
                    <strong>Permitted Areas:</strong>
                  </p>
                  <ul className="list-disc pl-5 text-sm text-gray-600 space-y-1">
                    <li>All major national parks and reserves</li>
                    <li>Public roads and designated tracks</li>
                    <li>Urban and rural areas within Uganda</li>
                  </ul>
                  <p className="text-sm text-gray-600 mt-3">
                    <strong>Restrictions:</strong>
                  </p>
                  <ul className="list-disc pl-5 text-sm text-gray-600 space-y-1">
                    <li>No off-road driving unless specifically permitted</li>
                    <li>No crossing international borders</li>
                    <li>No driving after sunset in national parks</li>
                    <li>No unauthorized modifications to vehicle</li>
                  </ul>
                </div>
              </section>

              {/* Accidents & Emergencies */}
              <section>
                <h4 className="flex items-center text-lg font-semibold text-gray-900 mb-3">
                  <AlertTriangle className="w-5 h-5 text-ta-green mr-2" />
                  Accidents & Emergencies
                </h4>
                <div className="bg-gray-50 p-4 rounded-lg space-y-2">
                  <p className="text-sm text-gray-600">
                    <strong>In Case of Accident:</strong>
                  </p>
                  <ul className="list-disc pl-5 text-sm text-gray-600 space-y-1">
                    <li>Contact emergency services immediately</li>
                    <li>Document incident with photos</li>
                    <li>Obtain police report if applicable</li>
                    <li>Contact our 24/7 support line</li>
                  </ul>
                  <p className="text-sm text-gray-600 mt-3">
                    <strong>Vehicle Breakdown:</strong>
                  </p>
                  <ul className="list-disc pl-5 text-sm text-gray-600 space-y-1">
                    <li>24/7 roadside assistance provided</li>
                    <li>Replacement vehicle subject to availability</li>
                    <li>Emergency contact numbers provided</li>
                  </ul>
                </div>
              </section>

              {/* Return Policy */}
              <section>
                <h4 className="flex items-center text-lg font-semibold text-gray-900 mb-3">
                  <Tool className="w-5 h-5 text-ta-green mr-2" />
                  Return Policies
                </h4>
                <div className="bg-gray-50 p-4 rounded-lg space-y-2">
                  <ul className="list-disc pl-5 text-sm text-gray-600 space-y-1">
                    <li>Vehicle must be returned with full fuel tank</li>
                    <li>Interior must be clean and free of excessive dirt</li>
                    <li>All equipment must be returned in original condition</li>
                    <li>Late returns charged at hourly rate</li>
                    <li>Vehicle inspection required at return</li>
                  </ul>
                </div>
              </section>
            </div>
          </div>

          <div className="px-6 py-4 bg-gray-50 border-t border-gray-200">
            <div className="flex items-center justify-between">
              <button
                onClick={onClose}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
              >
                Close
              </button>
              <button
                onClick={onAccept}
                className="px-4 py-2 text-sm font-medium text-white bg-ta-green rounded-md hover:bg-ta-green-dark"
              >
                Accept Terms & Conditions
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SelfDriveTermsModal;